import React, { useEffect, useState } from 'react';
import { Container, Paper, Typography, Box, Button, CircularProgress, Alert } from '@mui/material';
import { CheckCircle, Refresh } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { orderService } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { toast } from 'react-toastify';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const handleManualPaymentCheck = async () => {
    try {
      setCheckingPayment(true);
      // For now, let's just reload orders - in a real scenario, you'd verify payment status
      toast.info('Payment verification completed. Please check your orders.');
      navigate('/orders');
    } catch (error) {
      console.error('Error checking payment:', error);
      toast.error('Failed to verify payment status');
    } finally {
      setCheckingPayment(false);
    }
  };

  useEffect(() => {
    let isProcessing = false;
    
    // Handle payment success callback
    const handlePaymentCallback = async () => {
      if (isProcessing) return;
      isProcessing = true;
      
      try {
        // Log all URL parameters received from PayU
        //console.log('=== PaymentSuccess Page Loaded ===');
        //console.log('Current URL:', window.location.href);
        //console.log('Search Params String:', searchParams.toString());
        //console.log('All URL Parameters:');
        for (const [key, value] of searchParams.entries()) {
          //console.log(`  ${key}: ${value}`);
        }
        //console.log('===================================');
        
  const orderId = searchParams.get('udf1');
        const txnid = searchParams.get('txnid');
        const status = searchParams.get('status');
        
        //console.log('Extracted PayU data:', { orderId, txnid, status });
        
        // Check if this is a direct access (manual testing) or has payment parameters
        if (searchParams.toString() === '') {
          // Manual access - show success page without validation
          //console.log('Direct access to payment success page - showing success');
          setLoading(false);
          return;
        }
        
        if (orderId && status === 'success') {
          //console.log('Payment success indicated by gateway, verifying order paymentStatus...');
          try {
            // If legacy direct order OR transformed session id (ORD...), fetch order
            if(orderId.startsWith('ORD')){
              const response = await orderService.getOrder(orderId);
              const fetchedOrder = response.data.order;
              setOrderData(fetchedOrder);

              if (fetchedOrder && (fetchedOrder.paymentStatus === 'paid' || fetchedOrder.paymentStatus === 'completed')) {
                //console.log('Order is marked paid/completed. Proceeding to clear cart.');
                await clearCart();
                toast.success('Payment successful! Your order has been confirmed.');
              } else {
                //console.log('Order not yet marked paid/completed. Not clearing cart yet.');
                toast.info('Payment captured. Finalizing order... You can refresh status shortly.');
              }
            } else if(orderId.startsWith('SES')) {
              // Session id indicates backend may not have created order yet
              toast.info('Payment received. Creating order record... please refresh in a few seconds.');
            }
          } catch (orderError) {
            console.error('Error fetching order details for verification:', orderError);
            toast.info('Payment received. Order finalization pending. Check Orders later.');
          }
        } else {
          // Payment was not successful, redirect to failure page
          //console.log('Payment verification failed, redirecting to failure page:', { orderId, status });
          navigate('/payment-failure?' + searchParams.toString());
          return;
        }
      } catch (error) {
        console.error('Error handling payment success:', error);
        toast.error('Failed to verify payment');
        // Don't navigate to failure page on error, just show success
      } finally {
        setLoading(false);
      }
    };

    handlePaymentCallback();
  }, []); // Remove dependencies to prevent infinite loops

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Verifying payment...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Payment Successful!
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Thank you for your order
        </Typography>
        
        {!orderData && (
          <Alert severity="info" sx={{ my: 3 }}>
            <Box>
              <Typography variant="body2" gutterBottom>
                If your payment was successful but not reflected in your orders, click the button below to refresh.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleManualPaymentCheck}
                disabled={checkingPayment}
                sx={{ mt: 1 }}
              >
                {checkingPayment ? 'Checking...' : 'Verify Payment Status'}
              </Button>
            </Box>
          </Alert>
        )}

        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button 
            variant="contained" 
            onClick={() => navigate('/orders')}
          >
            View Orders
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/')}
          >
            Continue Shopping
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default PaymentSuccess;
